import { HighlightArea } from "@components/HighlightTextArea/HighlightTextArea"
import { isEqual, uniqWith } from "lodash"
import { YAMLMap, YAMLSeq, parseDocument } from "yaml"

type ErrorPosition = {
  groupIdx: number
  chartIdx?: number // not present means error is for whole group itself
}

type GroupChartArea = HighlightArea & { charts: HighlightArea[] }

/**
 * We can either get error messages for group and chart, or just for group
 * a) (n:m) of an error signifies the n-group, and the m-chart within that group's Charts block
 * b) (n) of an error signifies the n-group itself
 *
 * E.g. "(0:1) Node 'n123' in Chart `Time series chart` not found in graph"
 */
function getPositionFromErrorMessage(error: string): ErrorPosition | null {
  const groupAndChartIndexRegex = /^.*\((\d+):(\d+)\)/ // group and chart references
  const [, groupIdxStr, chartIdxStr] = error.match(groupAndChartIndexRegex) || []
  if (groupIdxStr && chartIdxStr) {
    const groupIdx = parseInt(groupIdxStr)
    const chartIdx = parseInt(chartIdxStr)
    if (!Number.isInteger(groupIdx) || !Number.isInteger(chartIdx)) return null

    return { groupIdx, chartIdx } //return to programmer's index
  }

  const regexIndexOnly = /^.*\((\d+)\)/ // group only references
  const [, groupOnlyIdx] = error.match(regexIndexOnly) || []
  if (groupOnlyIdx) {
    const groupIdx = parseInt(groupOnlyIdx)
    if (!Number.isInteger(groupIdx)) return null

    return { groupIdx } //return to programmer's index
  }

  return null
}

const getDashboardGraphsChartsYAMLAreas = (text: string): GroupChartArea[] => {
  const doc = parseDocument(text)

  const groupsSequence = doc.get("Groups") as YAMLSeq | undefined
  if (!groupsSequence) return []

  const groupChartAreas: GroupChartArea[] = []

  for (const group of groupsSequence.items as YAMLMap[]) {
    if (!group.range) continue

    const groupArea: GroupChartArea = {
      startIndex: group.range[0],
      endIndex: group.range[1],
      charts: [],
    }
    groupChartAreas.push(groupArea)

    const chartsSequence = group.get("Charts") as YAMLSeq | undefined
    if (!chartsSequence) continue

    for (const chart of chartsSequence.items as YAMLMap[]) {
      if (!chart.range) continue

      const chartArea: HighlightArea = { startIndex: chart.range[0], endIndex: chart.range[1] }
      groupArea.charts.push(chartArea)
    }
  }

  return groupChartAreas
}

export const getHighlightedAreas = (text: string, errors: string[]): HighlightArea[] => {
  const groupsChartAreas = getDashboardGraphsChartsYAMLAreas(text)

  const errorsIndexes = errors.reduce<ErrorPosition[]>((result, error) => {
    const errorPosition = getPositionFromErrorMessage(error)
    if (!errorPosition) return result

    return [...result, errorPosition]
  }, [])

  const areasToHighlight = errorsIndexes.reduce<HighlightArea[]>(
    (result, { groupIdx, chartIdx }) => {
      const groupArea = groupsChartAreas[groupIdx]
      if (!groupArea) return result

      // no chartIdx means whole group should be marked as invalid
      if (chartIdx === undefined) {
        return [...result, { startIndex: groupArea.startIndex, endIndex: groupArea.endIndex }]
      }

      const chartArea = groupArea.charts[chartIdx]
      if (!chartArea) return result

      return [...result, { startIndex: chartArea.startIndex, endIndex: chartArea.endIndex }]
    },
    [],
  )

  return uniqWith(areasToHighlight, isEqual)
}
